import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { getSuggestedBrands } from '../../api/brands/suggestedBrands';
import { searchBrandByLabel } from '../../common/api/searchBrandByLabel';
import { brands2 } from '../../common/data/brandsData2';
import { Brand } from '../../common/types';
import { trackEvent } from '../../common/utils/analytics';
import BrandButton from '../buttons/BrandButton';
import BrandButton2 from '../buttons/BrandButton2';
import PrimaryTextSmall from '../texts/PrimaryTextSmall';

interface SearchBarProps {
  onBrandSelect: (brand: Brand) => void;
  selectedBrands: Brand[];
  setSelectedBrands: React.Dispatch<React.SetStateAction<Brand[]>>;
  isSelected: (brandCode: string) => boolean;
  onUrlSelect?: (url: string) => void;
  partnerId?: string;
}

const BrandSearchInput: FC<SearchBarProps> = ({ onBrandSelect,
  onUrlSelect,
  selectedBrands,
  setSelectedBrands,
  isSelected,
  partnerId }) => {
  const { t, i18n } = useTranslation();
  // const { uid } = useAuthContext();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [isListVisible, setIsListVisible] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchStartedRef = useRef(false);
  //const [selectedBrands, setSelectedBrands] = useState<Array<Brand>>([]);
  const [suggestedBrands, setSuggestedBrands] = useState<Array<Brand>>([]);
  useEffect(() => {
    const loadSuggestedBrands = async () => {
      try {
        if (partnerId) {
          const brands = await getSuggestedBrands(partnerId);
          setSuggestedBrands((brands && brands.length > 0) ? brands : brands2);
        }
      } catch (error) {
        console.error('Failed to load suggested brands:', error);
        setSuggestedBrands(brands2);
      }
    };

    loadSuggestedBrands();
  }, [partnerId]);

  const isUrl = (text: string): boolean => {
    const urlRegex = /^((https?:\/\/)?([\w-]+\.)+[\w-]{2,})(\/.+)$/i;
    return urlRegex.test(text.trim());
  };

  const fetchResults = async (search: string) => {
    if (!search.trim()) return;

    trackEvent('input_entries', { query: search });
    setLoading(true);
    try {
      console.debug("🔍 Searching brands with:", search);
      console.debug("🌐 Language:", i18n.language);

      const res = await searchBrandByLabel(search, i18n.language);
      console.debug("✅ Brand search result:", res);

      setResults(res);
    } catch (e) {
      console.error("❌ Brand search failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setQuery(text);
    setIsListVisible(true);
    setSelectedUrl(null);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (text.trim() && !searchStartedRef.current) {
      trackEvent('brand_search_start');
      searchStartedRef.current = true;
    } else if (!text.trim()) {
      searchStartedRef.current = false;
    }

    if (isUrl(text)) {
      setResults([]);
      return;
    }

    searchTimeout.current = setTimeout(() => {
      fetchResults(text);
    }, 400);
  };

  const handleSelect = (brand: Brand, rank?: number, source?: 'search' | 'suggested') => {
    if (isSelected(brand.code)) {
      setSelectedBrands(prev => prev.filter(b => b.code !== brand.code));
    } else if (selectedBrands.length < 3) {
      setSelectedBrands(prev => [...prev, brand]);
      if (source === 'search') {
        trackEvent('brand_search_end');
      }
    }
    onBrandSelect(brand);
    setQuery('');
    setResults([]);
    setIsListVisible(false);
    searchStartedRef.current = false;
    if (rank !== undefined) {
      trackEvent('brand_selection_number', { rank });
    }
  };

  const handleRemove = (brandCode: string) => {
    setSelectedBrands((prev) => prev.filter((b) => b.code !== brandCode));
  };

  const handleUrlSelect = (url: string) => {
    if (!selectedUrl) {
      setSelectedUrl(url);
      onUrlSelect?.(url);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      if (searchStartedRef.current) {
        trackEvent('brand_search_close');
        searchStartedRef.current = false;
      }
      setIsListVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="pop-relative pop-w-full pop-mx-auto pop-mb-6" ref={dropdownRef}>
      <div className="pop-flex pop-items-center pop-w-full pop-border-1 pop-rounded-full pop-bg-white">
        {query && (
        <button
          type="button"
          onClick={() => {
            if (searchStartedRef.current) {
              trackEvent('brand_search_close');
            }
            setQuery('');
            setIsListVisible(false);
            setResults([]);
            searchStartedRef.current = false;
          }}
          className="pop-absolute pop-text-popsizePrimary90 pop-z-full pop-right-5"
        >
          <IoClose size={18} fill="rgb(var(--pop-secondary-90))"/>
        </button>
      )}
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={t('brand_search_placeholder') as string}
          className="pop-w-full pop-text-center pop-border-1 pop-border-popsizeSecondary90 pop-rounded-full pop-p-2 pop-text-popsizePrimary90 focus:pop-outline-none pop-bg-white"
        />
      </div>

      {isListVisible && (
        <ul className="pop-absolute pop-z-10 pop-mt-3 pop-bg-popsizeSecondary10 pop-border-1 pop-border-popsizePrimary20 pop-rounded-xl pop-w-full pop-max-h-60 pop-overflow-y-auto">
          {loading && (
            <li className="pop-p-2 pop-text-popsizePrimary50">{t('loading')}...</li>
          )}
          {!loading && isUrl(query) && (
            <li
              onClick={() => handleUrlSelect(query)}
              className={`pop-p-2 pop-cursor-pointer hover:pop-bg-popsizeSecondary10 ${
                selectedUrl === query ? 'pop-text-popsizePrimary20' : ''
              }`}
            >
              {query}
              {selectedUrl === query && <span className="pop-ml-2">🔄</span>}
            </li>
          )}
          {!loading && results.map((brand) => {
            return (
              <li
                key={brand.code}
                onClick={() => handleSelect(brand, undefined, 'search')}
                className="pop-flex pop-items-center pop-gap-2 pop-p-2 pop-cursor-pointer hover:pop-bg-popsizeSecondary10"
              >
                {brand.faviconUrl && (
                  <img
                    src={brand.faviconUrl}
                    alt="icon"
                    className="pop-w-2 pop-h-auto"
                  />
                )}

                <div>
                  <div className="pop-text-sm pop-font-semibold pop-text-popsizePrimary90">
                    {brand.name}
                  </div>
                  {/* keep the domain subtitle if you still want it */}
                  {brand.domain?.trim() && (
                    <div className="pop-text-xs pop-text-popsizePrimary50">
                      {brand.domain}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {!loading && !isUrl(query) && query.length > 0 && results.length === 0 && (
            <li className="pop-p-2 pop-text-popsizePrimary50">{t('no_results_found')}</li>
          )}
        </ul>
      )}
      <div className={'pop-flex pop-flex-col pop-w-full pop-my-8 pop-items-center pop-justify-center'}>
        <div className="pop-w-full pop-overflow-x-auto">
          <div className="pop-flex pop-gap-4 pop-flex-nowrap pop-justify-center pop-w-max">
              {suggestedBrands.map((brand, index) => (
                <div
                  key={index}
                  className="pop-flex pop-flex-col pop-items-center pop-gap-1 pop-w-20 pop-shrink-0 pop-gap-2"
                >
                <BrandButton
                  key={index}
                  onClick={() => handleSelect(brand, index + 1, 'suggested')}
                  isSelected={isSelected(brand.code)}
                  className="pop-flex pop-flex-col pop-items-center pop-gap-2"
                >
                {brand.image ? (<img
                  src={`${process.env.REACT_APP_GCP_BUCKET_URL}/images/brand-logos/${brand.image}`}
                  alt={brand.name}
                  className="pop-w-16 pop-h-auto pop-object-contain"
                  onError={e => {
                    if (brand.faviconUrl) {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = brand.faviconUrl;
                    }
                  }}
                  />
                ) : brand.faviconUrl ? (
                  <div className="pop-flex pop-flex-col pop-items-center pop-w-full">
                    <img
                      src={brand.faviconUrl}
                      alt={brand.name}
                      title={brand.name}
                      className="pop-w-6 pop-h-6 pop-mx-auto"
                    />
                  </div>
                ) : (
                  <div className="pop-text-xs pop-text-popsizePrimary50 pop-text-center">
                    {brand.name}
                  </div>
                )}
              </BrandButton>
              <div className="pop-text-xs pop-text-center pop-text-popsizePrimary90 pop-max-w-full pop-truncate">
                {brand.name || ''}
              </div>
          </div>
              ))}
            </div>
          </div>
        </div>
      {selectedBrands.length > 0 && (
        <div className="pop-w-full pop-mt-4">
          <div className="pop-flex pop-items-center pop-gap-4 pop-flex-wrap pop-justify-center">
          <PrimaryTextSmall className="pop-text-center pop-mt-0">
            {t('your_selected_brands')}
          </PrimaryTextSmall>
          <div className="pop-flex pop-flex-wrap pop-gap-2 pop-justify-center pop-items-center pop-bg-popsizeSecondary10  pop-rounded-xl pop-py-4">
            {selectedBrands.map((brand) => (
              <div key={brand.code} className="pop-flex pop-flex-col pop-items-center">
                <BrandButton2
                  onClick={() => handleSelect(brand)}
                  isSelected={isSelected(brand.code)}
                  className="pop-flex pop-flex-col pop-items-center pop-gap-2"
                  onDelete={() => handleRemove(brand.code)}
                >
                  {brand.image ? (
                    <img
                      src={`${process.env.REACT_APP_GCP_BUCKET_URL}/images/brand-logos/${brand.image}`}
                      alt={brand.name}
                      className="pop-w-4 pop-h-auto pop-object-contain"
                    />
                  ) : brand.faviconUrl ? (
                    <div className="pop-flex pop-flex-col pop-items-center pop-w-full">
                      <img
                        src={brand.faviconUrl}
                        alt={brand.name}
                        title={brand.name}
                        className="pop-w-4 pop-h-4 pop-mx-auto"
                      />
                      {/* <div className="pop-text-2xs pop-text-center pop-text-popsizePrimary90 pop-w-full pop-px-1 pop-truncate pop-overflow-hidden" style={{maxWidth: '100%'}}>
                        {brand.name}
                      </div> */}
                    </div>
                  ) : (
                    <div className="pop-text-xs pop-text-popsizePrimary50 pop-text-center">
                      {brand.name}
                    </div>
                  )}
                  <div className="pop-text-2xs pop-text-center pop-text-popsizePrimary90 truncate">
                    {''}
                  </div>
                </BrandButton2>
              </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandSearchInput;
